import { colors, sizes } from '@lmc/common/constants'
import { NoDataFound } from '../../../../components/generic'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { history } from '../../../../store'
import { withRouter } from '../../../../withRouter'
import {
  connect,
  MapDispatchToPropsParam,
  MapStateToPropsParam,
} from 'react-redux'
import Operations from '../../../../operations'
import { Need, ResidentNeed } from '@lmc/ts-common'
import { RouterProps } from '@lmc/common/types'
import {
  NeedsModal,
  ResidentNeedsHeaderSection,
  ResidentNeedsTable,
} from './components'
import { DeleteSkillNeedModal } from '../../../../screens/AccountsSettings/SkillsAndNeeds/components'
import { Text } from '@lmc/ui-kit'
import { getResidentNeeds } from 'selectors/NeedsAndSkills'
import useResident from 'hooks/useResident'

export type ProfileNeedOption = { name: string; needs: Need[] }

interface StateProps {
  needs?: ResidentNeed[]
  profileNeedOptions?: ProfileNeedOption[]
  carePlanNominative?: string
  preferredPeopleNominative?: string
}

interface DispatchProps {
  fetchResidentNeedOptions?: () => Promise<unknown>
  fetchResidentNeeds?: (id: string) => Promise<unknown>
  addNeed?: (need: ResidentNeed, id: string) => Promise<unknown>
  updateNeed?: (
    need: ResidentNeed,
    id: string,
    needId: string
  ) => Promise<unknown>
  deleteNeed?: (needId: string, residentId: string) => Promise<unknown>
  router: RouterProps
}

type Props = StateProps & DispatchProps

export function ResidentNeedsScreen({
  fetchResidentNeedOptions,
  addNeed,
  updateNeed,
  deleteNeed,
  needs,
  profileNeedOptions,
  router,
  carePlanNominative,
  preferredPeopleNominative,
  fetchResidentNeeds,
}: Props): React.ReactElement {
  const { t } = useTranslation()

  const handleView = () => history.push(`/residents`)

  const residentId = router.params.id

  const [open, setOpen] = useState(false)

  const [loading, setLoading] = useState(true)

  const [openDelete, setOpenDelete] = useState(false)

  const [profileNeedDetail, setProfileNeedDetail] = useState<ResidentNeed>()

  const { resident, isLoading } = useResident({ id: residentId })

  const residentName = isLoading
    ? undefined
    : `${resident?.name?.first} ${resident?.name?.last}`

  const handleToggleModal = () => {
    if (open) {
      setProfileNeedDetail(undefined)
    }
    setOpen(!open)
  }

  const handleToogleDeleteModal = () => {
    setOpenDelete(!openDelete)
    setOpen(false)
  }

  useEffect(() => {
    fetchResidentNeedOptions?.()
    getResidentNeeds()
  }, [residentId])

  const hasNeeds = !!needs?.length

  const needOptions: Need[] = []

  profileNeedOptions?.forEach((item) =>
    item.needs?.forEach((need) => needOptions.push(need))
  )

  const getResidentNeeds = async () => {
    await fetchResidentNeeds?.(residentId).then(() => {
      setLoading(false)
    })
  }

  const handleDelete = async () => {
    try {
      const res = await deleteNeed?.(
        profileNeedDetail?.id as string,
        residentId
      )
      if (res) {
        setProfileNeedDetail(undefined)
        handleToogleDeleteModal()
        handleToggleModal()
        getResidentNeeds()
        setOpen(false)
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e)
    }
  }

  return (
    <>
      <DeleteSkillNeedModal
        isModalVisible={openDelete}
        handleToogleDeleteModal={handleToogleDeleteModal}
        onConfirm={handleDelete}
        title={t('skillsAndNeeds.areYouSureNeed')}
      >
        <Text as="p2">
          {t('skillsAndNeeds.byRemovingNeed', {
            variable: preferredPeopleNominative?.toLowerCase(),
          })}
        </Text>
      </DeleteSkillNeedModal>
      <NeedsModal
        isModalVisible={open}
        handleToogleDeleteModal={handleToogleDeleteModal}
        handleToggleModal={handleToggleModal}
        addNeed={addNeed}
        updateNeed={updateNeed}
        profileNeedOptions={profileNeedOptions}
        residentId={residentId}
        profileNeedDetail={profileNeedDetail}
        needs={needs}
        carePlanNominative={carePlanNominative}
      />
      <div style={styles.container}>
        <ResidentNeedsHeaderSection
          residentName={residentName}
          handleToggleModal={handleToggleModal}
          handleView={handleView}
        />
        {!loading && hasNeeds ? (
          <ResidentNeedsTable
            loading={loading}
            needs={needs}
            needOptions={needOptions}
            carePlanNominative={carePlanNominative as string}
            setProfileNeedDetail={setProfileNeedDetail}
          />
        ) : (
          <NoDataFound
            heading={t(`skillsAndNeeds.nothingToSee`)}
            text={t(`skillsAndNeeds.addNeedsToProfile`)}
            linkText={t(`skillsAndNeeds.findOutMore`)}
            url="https://support.logmycare.co.uk/en/articles/6987558-how-to-add-needs-for-your-service-users"
          />
        )}
      </div>
    </>
  )
}

const styles = {
  container: {
    margin: '0 auto',
    maxWidth: sizes.childScreen,
  },
  iconHandle: {
    rotate: '180deg',
  },
  detailLeft: {
    display: 'inline-flex',
    gap: '.5rem',
    fontWeight: 'bold',
    cursor: 'pointer',
    margin: '0 0 24px 0',
  },
  linkColor: {
    color: colors.lightpink,
  },
}

export const mapStateToProps = (
  state: {
    data: {
      needs: ResidentNeed[]
      ['skill-need-templates/categories']: Need[]
    }
    global: {
      home: {
        preferredCarePlansNominative: string
        preferredPeopleNominative: string
      }
    }
  },
  props: { router: { params: { id: string } } }
) => ({
  needs: getResidentNeeds(state, props),
  profileNeedOptions: state.data['skill-need-templates/categories'],
  carePlanNominative: state.global.home.preferredCarePlansNominative,
  preferredPeopleNominative: state.global.home.preferredPeopleNominative,
})

const mapDispatchToProps = (dispatch: (arg0: unknown) => unknown) => ({
  fetchResidentNeedOptions: () =>
    dispatch(
      Operations.loadList('skill-need-templates/categories', { needs: 1 })
    ),
  fetchResidentNeeds: (id: string) =>
    dispatch(
      Operations.loadList(`needs`, '', true, `/api/list/residents/${id}/needs`)
    ),
  addNeed: (need: ResidentNeed, id: string) =>
    dispatch(
      Operations.createListItem({
        listId: `needs`,
        itemId: '',
        json: need,
        url: `list/residents/${id}/needs`,
      })
    ),
  updateNeed: (need: ResidentNeed, id: string, needId: string) =>
    dispatch(
      Operations.patchListItem({
        listId: `needs`,
        itemId: '',
        json: need,
        url: `list/residents/${id}/needs/${needId}`,
      })
    ),
  deleteNeed: (needId: string, residentId: string) =>
    dispatch(
      Operations.deleteListItem({
        listId: `needs`,
        itemId: '',
        url: `list/residents/${residentId}/needs/${needId}`,
      })
    ),
})

export default withRouter(
  connect<StateProps, DispatchProps, Props>(
    mapStateToProps as unknown as MapStateToPropsParam<
      StateProps,
      Props,
      unknown
    >,
    mapDispatchToProps as unknown as MapDispatchToPropsParam<
      DispatchProps,
      Props
    >
  )(ResidentNeedsScreen)
)
